import {
  ChangeDetectionStrategy,
  Component,
  signal,
  viewChild,
} from '@angular/core';
import {
  SignalTextarea,
  SignalTextField,
  type TriggerActivatedEvent,
} from '@vallift/ngl';
import {
  BoundTextSlashMenuComponent,
  type SlashMenuAnchorRect,
  type SlashMenuItem,
} from '../../forms/components/bound-text-slash-menu/bound-text-slash-menu';
import { formatBoundTextToken } from '../../forms/utils/bound-text-template';

@Component({
  selector: 'app-slash-caret-demo',
  standalone: true,
  imports: [SignalTextarea, SignalTextField, BoundTextSlashMenuComponent],
  templateUrl: './slash-caret-demo.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full max-w-xl' },
})
export class SlashCaretDemoComponent {
  private readonly textarea = viewChild(SignalTextarea);
  private readonly textField = viewChild(SignalTextField);

  readonly textareaValue = signal('');
  readonly fieldValue = signal('');

  readonly slashMenuOpen = signal(false);
  readonly slashQuery = signal('');
  readonly slashAnchor = signal<SlashMenuAnchorRect | null>(null);

  private pending: TriggerActivatedEvent | null = null;
  private pendingField: 'textarea' | 'field' | null = null;

  onTextareaValueChange(value: string): void {
    this.textareaValue.set(value);
    this.syncSlashMenuFromValue(value);
  }

  onFieldValueChange(value: string): void {
    this.fieldValue.set(value);
    this.syncSlashMenuFromValue(value);
  }

  onTextareaTrigger(event: TriggerActivatedEvent): void {
    this.openMenu(event, 'textarea');
  }

  onFieldTrigger(event: TriggerActivatedEvent): void {
    this.openMenu(event, 'field');
  }

  onSlashSelect(item: SlashMenuItem): void {
    if (!this.pending || !this.pendingField) {
      this.closeSlashMenu();
      return;
    }
    const event = this.pending;
    const field = this.pendingField;
    const token = formatBoundTextToken(item.namespace, item.key, item.title);
    const triggerLen = event.trigger.length + (event.query?.length ?? 0);
    const start = Math.max(0, event.caretIndex - triggerLen);
    const current =
      field === 'textarea' ? this.textareaValue() : this.fieldValue();
    const next =
      current.slice(0, start) + token + current.slice(event.caretIndex);
    const caret = start + token.length;
    this.closeSlashMenu();

    if (field === 'textarea') {
      this.textareaValue.set(next);
      queueMicrotask(() => this.textarea()?.focusHandlebars(caret));
    } else {
      this.fieldValue.set(next);
      queueMicrotask(() => this.textField()?.focusHandlebars(caret));
    }
  }

  onSlashDismiss(): void {
    this.closeSlashMenu();
  }

  private openMenu(
    event: TriggerActivatedEvent,
    field: 'textarea' | 'field',
  ): void {
    this.pending = event;
    this.pendingField = field;
    this.slashQuery.set(event.query ?? '');
    this.slashAnchor.set(this.rectFromTarget(this.visibleAnchor(event.target)));
    this.slashMenuOpen.set(true);
  }

  private syncSlashMenuFromValue(label: string): void {
    if (!this.slashMenuOpen() || !this.pending) {
      return;
    }
    const trigger = this.pending.trigger || '/';
    const slashIndex = label.lastIndexOf(trigger);
    if (slashIndex < 0) {
      this.closeSlashMenu();
      return;
    }
    const query = label.slice(slashIndex + trigger.length);
    if (/[\n\r{]/.test(query)) {
      this.closeSlashMenu();
      return;
    }
    this.pending = {
      ...this.pending,
      query,
      caretIndex: slashIndex + trigger.length + query.length,
    };
    this.slashQuery.set(query);
  }

  private closeSlashMenu(): void {
    this.slashMenuOpen.set(false);
    this.slashQuery.set('');
    this.slashAnchor.set(null);
    this.pending = null;
    this.pendingField = null;
  }

  private visibleAnchor(
    target: HTMLInputElement | HTMLTextAreaElement | HTMLElement,
  ): HTMLElement {
    const wrapped = (target as HTMLElement).closest?.(
      '[data-bound-text-slash-anchor]',
    );
    const visible = wrapped?.querySelector<HTMLElement>(
      '[contenteditable="true"], textarea:not([aria-hidden="true"]), input:not([aria-hidden="true"])',
    );
    return visible ?? (target as HTMLElement);
  }

  private rectFromTarget(target: HTMLElement): SlashMenuAnchorRect {
    const rect = target.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  }
}
